import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { KeyRequest } from '../types';
import { keyRequestsApi } from '../services/api';

interface KeyRequestsState {
  requests: KeyRequest[];
  loading: boolean;
  error: string | null;
}

const initialState: KeyRequestsState = {
  requests: [],
  loading: false,
  error: null,
};

// Async thunks
export const fetchKeyRequests = createAsyncThunk(
  'keyRequests/fetchKeyRequests',
  async (status: string | undefined, { rejectWithValue }) => {
    try {
      return await keyRequestsApi.getAll(status);
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

export const createKeyRequestAsync = createAsyncThunk(
  'keyRequests/createKeyRequest',
  async (request: Partial<KeyRequest>, { rejectWithValue }) => {
    try {
      return await keyRequestsApi.create(request);
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

export const approveKeyRequestAsync = createAsyncThunk(
  'keyRequests/approveKeyRequest',
  async ({ id, reviewNotes }: { id: string; reviewNotes?: string }, { rejectWithValue }) => {
    try {
      return await keyRequestsApi.approve(id, reviewNotes);
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

export const rejectKeyRequestAsync = createAsyncThunk(
  'keyRequests/rejectKeyRequest',
  async ({ id, reviewNotes }: { id: string; reviewNotes?: string }, { rejectWithValue }) => {
    try {
      return await keyRequestsApi.reject(id, reviewNotes);
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

export const fulfillKeyRequestAsync = createAsyncThunk(
  'keyRequests/fulfillKeyRequest',
  async ({ id, notes }: { id: string; notes?: string }, { rejectWithValue }) => {
    try {
      return await keyRequestsApi.fulfill(id, notes);
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

export const cancelKeyRequestAsync = createAsyncThunk(
  'keyRequests/cancelKeyRequest',
  async (id: string, { rejectWithValue }) => {
    try {
      await keyRequestsApi.cancel(id);
      return id;
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

const keyRequestsSlice = createSlice({
  name: 'keyRequests',
  initialState,
  reducers: {
    setKeyRequests: (state, action: PayloadAction<KeyRequest[]>) => {
      state.requests = action.payload;
    },
    addKeyRequest: (state, action: PayloadAction<KeyRequest>) => {
      state.requests.unshift(action.payload);
    },
    updateKeyRequest: (state, action: PayloadAction<KeyRequest>) => {
      const index = state.requests.findIndex(r => r.id === action.payload.id);
      if (index !== -1) {
        state.requests[index] = action.payload;
      }
    },
    removeKeyRequest: (state, action: PayloadAction<string>) => {
      state.requests = state.requests.filter(r => r.id !== action.payload);
    },
    clearKeyRequestsError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch requests
      .addCase(fetchKeyRequests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchKeyRequests.fulfilled, (state, action) => {
        state.loading = false;
        state.requests = action.payload;
      })
      .addCase(fetchKeyRequests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Create request
      .addCase(createKeyRequestAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createKeyRequestAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.requests.unshift(action.payload);
      })
      .addCase(createKeyRequestAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Approve request
      .addCase(approveKeyRequestAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(approveKeyRequestAsync.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.requests.findIndex(r => r.id === action.payload.id);
        if (index !== -1) {
          state.requests[index] = action.payload;
        }
      })
      .addCase(approveKeyRequestAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Reject request
      .addCase(rejectKeyRequestAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(rejectKeyRequestAsync.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.requests.findIndex(r => r.id === action.payload.id);
        if (index !== -1) {
          state.requests[index] = action.payload;
        }
      })
      .addCase(rejectKeyRequestAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fulfill request
      .addCase(fulfillKeyRequestAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fulfillKeyRequestAsync.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.requests.findIndex(r => r.id === action.payload.id);
        if (index !== -1) {
          state.requests[index] = action.payload;
        }
      })
      .addCase(fulfillKeyRequestAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Cancel request
      .addCase(cancelKeyRequestAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(cancelKeyRequestAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.requests = state.requests.filter(r => r.id !== action.payload);
      })
      .addCase(cancelKeyRequestAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setKeyRequests, addKeyRequest, updateKeyRequest, removeKeyRequest, clearKeyRequestsError } = keyRequestsSlice.actions;
export default keyRequestsSlice.reducer;
